import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { takeUntil } from 'rxjs/operators';
import { sweetAlert } from 'src/app/utils/swal';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { StaffService } from '../../staff-service/staff.service';

@Component({
  selector: 'app-add-staff-shift-override',
  templateUrl: './add-staff-shift-override.component.html',
  styleUrls: ['./add-staff-shift-override.component.css']
})
export class AddStaffShiftOverrideComponent implements OnInit, OnDestroy {
  user;
  staffList: any = [];
  staffId: any;
  shiftId: any;
  shiftOverrideForm: FormGroup;
  isAllowToView = false;

  constructor(private fb: FormBuilder, private sharedDataService: SharedDataService, private activatedRoute: ActivatedRoute, private staffService: StaffService, private router: Router) { }

  ngOnInit() {
    this.staffId = this.activatedRoute.snapshot.paramMap.get('id');
    this.shiftId = this.activatedRoute.snapshot.paramMap.get('overrideId');

    // Staff can only be picked when not opened from a specific staff member
    if (!this.staffId) {
      this.isAllowToView = true;
    }

    this.shiftOverrideForm = this.fb.group({
      staff: [''],
      dateOfService: [moment().startOf('day').toISOString()],
      isEnabled: [false],
      status: ['active', Validators.required],
      shift: this.fb.group({
        preCheckin: [moment().startOf('day').toISOString()],
        preCheckout: [moment().endOf('day').toISOString()],
        checkin: [moment().startOf('day').format("HH:mm"), Validators.required],
        checkout: [moment().endOf('day').format("HH:mm"), Validators.required],
      })
    });

    if (this.shiftId) {
      this.getShiftById();
    }

    this.getUser();
  }

  getUser() {
    this.sharedDataService.getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data) => {
        if (data) {
          this.user = data;
          if (this.staffId) {
            this.shiftOverrideForm.patchValue({
              staff: this.staffId
            });
          }

          this.getStaffList();
        }
      });
  }

  getStaffList() {
    this.staffService
      .getStaffList()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        if (data) {
          this.staffList = data.filter(staff => staff.accountStatus === 'active');
        }
      }, err => {
        sweetAlert('Error', err, 'warning', 'OK');
      });
  }

  getShiftById() {
    this.staffService.getShiftOverrideById(this.shiftId).pipe(takeUntil(componentDestroyed(this)))
      .subscribe((data: any) => {
        if (data) {
          const mCheckin = data.shift.checkin.split(":");
          const mCheckout = data.shift.checkout.split(":");

          this.shiftOverrideForm.patchValue({
            ...data,
            staff: data.staff && data.staff._id ? data.staff._id : data.staff,
            shift: {
              ...data.shift,
              preCheckin: moment().startOf('day').hours(mCheckin[0]).minutes(mCheckin[1]).seconds(0).toISOString(),
              preCheckout: moment().startOf('day').hours(mCheckout[0]).minutes(mCheckout[1]).seconds(0).toISOString()
            }
          });
        }
      });
  }

  get shiftForm() {
    return this.shiftOverrideForm.controls.shift as FormGroup;
  }

  get form() {
    return this.shiftOverrideForm.controls;
  }

  onChangeShiftStart(event: any) {
    const shift = this.shiftOverrideForm.get('shift') as FormGroup;
    shift.patchValue({
      checkin: moment(event).format('HH:mm')
    });

    // Auto-adjust end if invalid
    if (moment(event).isAfter(shift.value.preCheckout)) {
      shift.patchValue({
        checkout: moment(event).format('HH:mm')
      });
    }
  }

  onChangeShiftEnd(event: any) {
    const shift = this.shiftOverrideForm.get('shift') as FormGroup;
    shift.patchValue({
      checkout: moment(event).format('HH:mm')
    });

    // Auto-adjust start if invalid
    if (moment(event).isBefore(shift.value.preCheckin)) {
      shift.patchValue({
        checkin: moment(event).format('HH:mm')
      });
    }
  }

  addShift() {
    if (!this.shiftOverrideForm.value.staff) {
      sweetAlert('Error', 'Please select staff', 'warning', 'OK');
      return;
    }

    const payload = this.shiftOverrideForm.value;
    this.staffService.addShiftOverride(payload, this.shiftId)
      .pipe(takeUntil(componentDestroyed(this))).subscribe(data => {
        if (data) {
          if (this.staffId) {
            this.router.navigateByUrl(`/staff/${this.staffId}/shift-override`);
          } else {
            this.router.navigateByUrl(`/staff/shift-override`);
          }
        }
      }, err => {
        sweetAlert('Error', err, 'warning', 'OK');
      });
  }

  onEnableShift(event) {
    const isOff = event.target.checked;
    const shift = this.shiftOverrideForm.get('shift') as FormGroup;

    if (isOff) {
      const date = moment().startOf('day');
      const start = date.clone().hour(0).minute(0);
      const end = date.clone().hour(23).minute(59);

      shift.patchValue({
        preCheckin: start.toISOString(),
        checkin: start.format('HH:mm'),
        preCheckout: end.toISOString(),
        checkout: end.format('HH:mm')
      });
    } else {
      shift.patchValue({
        preCheckin: moment().startOf('day').toISOString(),
        preCheckout: moment().endOf('day').toISOString(),
        checkin: moment().startOf('day').format("HH:mm"),
        checkout: moment().endOf('day').format("HH:mm"),
      });
    }
  }

  getMinDate() {
    return moment().startOf('day');
  }

  getMaxDate() {
    return moment().endOf('day');
  }

  ngOnDestroy(): void {
  }

}
